import { useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';

interface Props {
  disabled: boolean;
  displayHtml?: string;
  displayContent?: React.ReactNode;
  savedValue: string;
  onSave: (newValue: string) => void;
  onEditModeChange: (state: boolean) => void;
  placeholder?: string;
  placeholderDisplay?: 'always' | 'hover';
  style?: React.CSSProperties;
}

const CardContentEditable: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  disabled,
  displayHtml,
  displayContent,
  savedValue,
  onSave,
  onEditModeChange,
  placeholder,
  placeholderDisplay = 'always',
  style,
}: Props) => {
  const theme = useTheme();
  const editCanceled = useRef(false);
  const contentEditableRef = useRef<HTMLElement | null>(null);
  const [mouseOverContent, setMouseOverContent] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentValue, setCurrentValue] = useState(savedValue);

  useEffect(() => {
    setCurrentValue(savedValue);
  }, [savedValue]);

  useEffect(() => {
    if (editMode) {
      contentEditableRef.current?.focus();
    }
  }, [editMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!editMode) {
      // If the edit mode was not active, do nothing
      console.warn('handleBlur called when editMode is false');
      return;
    }
    const valueToSave = e.target.innerText;

    setEditMode(false);

    if (editCanceled.current || valueToSave === savedValue || valueToSave === placeholder) {
      editCanceled.current = false;
      return;
    }

    onSave(valueToSave);
  };

  const cancelEdit = () => {
    setCurrentValue(savedValue);
    // The current value is not yet updated when entering the handleBlur function, so a ref is needed
    editCanceled.current = true;

    onEditModeChange(false);
    (document.activeElement as HTMLInputElement).blur();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      cancelEdit();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onEditModeChange(false);
      (document.activeElement as HTMLInputElement).blur();
    }
  };

  const shouldShowEditingChrome = (mouseOverContent || editMode) && !disabled;
  const contentMargin = shouldShowEditingChrome ? 0 : 1;

  const contentEditableStyle = {
    width: 'fit-content',
    minWidth: 16,
    padding: '0 2px',
    borderStyle: 'dashed',
    outline: 'none',
    backgroundColor: editMode
      ? theme.palette.mode === 'light'
        ? theme.palette.grey[200]
        : theme.palette.grey[800]
      : 'transparent',
    marginTop: contentMargin,
    marginRight: contentMargin,
    marginBottom: contentMargin,
    marginLeft: contentMargin,
    borderWidth: shouldShowEditingChrome ? 1 : 0,
    cursor: mouseOverContent && !disabled ? 'text' : 'inherit',
    ...(style || {}),
  };

  const shouldShowPlaceholder =
    !currentValue && Boolean(placeholder) && (editMode || placeholderDisplay === 'always' || mouseOverContent);

  const sharedDisplayProps = {
    style: contentEditableStyle,
    onClick: (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        event.stopPropagation();
        setEditMode(true);
        onEditModeChange(true);
      }
    },
    onMouseEnter: () => setMouseOverContent(true),
    onMouseLeave: () => setMouseOverContent(false),
  };

  if (!editMode && currentValue && displayContent) {
    return <div {...sharedDisplayProps}>{displayContent}</div>;
  }

  return (
    <ContentEditable
      innerRef={contentEditableRef}
      style={contentEditableStyle}
      onClick={sharedDisplayProps.onClick}
      html={
        editMode
          ? currentValue || ''
          : currentValue
          ? displayHtml || currentValue
          : shouldShowPlaceholder
          ? placeholder || ''
          : ''
      }
      disabled={disabled}
      onChange={handleChange}
      onMouseEnter={() => setMouseOverContent(true)}
      onMouseLeave={() => setMouseOverContent(false)}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
    />
  );
};

export default CardContentEditable;
